import { WrapperWidget } from '@agentselly/ui';
import { EmptyState } from '~/components/empty/EmptyState';
import { T } from '@tolgee/react';
import { clsx } from 'clsx';
import { TrendingDown, TrendingUp, Minus } from 'lucide-react';

interface Props {
  readonly value: number | null;
  readonly link: string;
  readonly showDemoWidgetContent?: boolean;
  readonly v3Redesign?: boolean;
}

export function MarketDynamicWidget(props: Props) {
  if (
    (props.value === null || props.value === undefined) &&
    !props.showDemoWidgetContent
  ) {
    return (
      <WrapperWidget>
        <EmptyState
          title={<T>widget_market_dynamic_title</T>}
          description={
            props.v3Redesign ? (
              <T keyName="widget_market_dynamic_no_data">
                Keine Daten – für diese Lage liefert die Quelle keinen Wert.
              </T>
            ) : (
              <T>empty_no_data_available</T>
            )
          }
        />
      </WrapperWidget>
    );
  }

  const marketValue = props.showDemoWidgetContent
    ? 0.4
    : (props.value as number);

  // Determine market condition
  const isBuyerMarket = marketValue < 0.4;
  const isSellerMarket = marketValue > 0.6;

  // Color and icon based on market condition
  const getMarketIndicator = () => {
    if (isBuyerMarket) {
      return {
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-50 dark:bg-green-900/30',
        borderColor: 'border-green-200 dark:border-green-800',
        icon: TrendingDown,
        label: <T>widget_market_dynamic_buyers_market</T>,
        description: <T>widget_market_dynamic_buyers_description</T>,
      };
    }
    if (isSellerMarket) {
      return {
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-50 dark:bg-red-900/30',
        borderColor: 'border-red-200 dark:border-red-800',
        icon: TrendingUp,
        label: <T>widget_market_dynamic_sellers_market</T>,
        description: <T>widget_market_dynamic_sellers_description</T>,
      };
    }
    // isBalanced (0.4 - 0.6 range)
    return {
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/30',
      borderColor: 'border-blue-200 dark:border-blue-800',
      icon: Minus,
      label: <T>widget_market_dynamic_balanced_market</T>,
      description: <T>widget_market_dynamic_balanced_description</T>,
    };
  };

  const indicator = getMarketIndicator();
  const IconComponent = indicator.icon;

  return (
    <WrapperWidget>
      <div
        className={clsx('h-full flex flex-col gap-y-4', {
          'opacity-50': props.showDemoWidgetContent,
        })}
      >
        <div className="font-bold">
          <h1 className="text-lg">
            <T>widget_market_dynamic_title</T>
          </h1>
        </div>

        {/* Main market indicator */}
        <div
          className={clsx(
            'rounded-lg p-4 border-2 transition-all duration-300',
            indicator.bgColor,
            indicator.borderColor,
          )}
        >
          <div className="flex items-center justify-center gap-3 mb-3">
            <IconComponent className={clsx('w-8 h-8', indicator.color)} />
            <div className="text-center">
              <div className={clsx('text-xl font-bold', indicator.color)}>
                {indicator.label}
              </div>
              <div
                className={clsx(
                  'text-sm text-gray-600',
                  props.v3Redesign
                    ? 'dark:text-zinc-400'
                    : 'dark:text-gray-400',
                )}
              >
                {indicator.description}
              </div>
            </div>
          </div>

          {/* Market value bar */}
          <div
            className={clsx(
              'relative h-3 bg-gray-200 rounded-full overflow-hidden',
              props.v3Redesign ? 'dark:bg-zinc-800' : 'dark:bg-gray-700',
            )}
          >
            <div
              className={clsx(
                'h-full transition-all duration-700 ease-out',
                isBuyerMarket
                  ? 'bg-green-500'
                  : isSellerMarket
                  ? 'bg-red-500'
                  : 'bg-blue-500',
              )}
              style={{ width: `${marketValue * 100}%` }}
            />
            {/* Indicator markers */}
            <div
              className={clsx(
                'absolute top-0 left-[40%] w-0.5 h-full opacity-50 bg-gray-400',
                props.v3Redesign ? 'dark:bg-zinc-500' : 'dark:bg-gray-500',
              )}
            />
            <div
              className={clsx(
                'absolute top-0 left-[60%] w-0.5 h-full opacity-50 bg-gray-400',
                props.v3Redesign ? 'dark:bg-zinc-500' : 'dark:bg-gray-500',
              )}
            />
          </div>

          {/* Labels */}
          <div
            className={clsx(
              'flex justify-between mt-2 text-xs text-gray-600',
              props.v3Redesign ? 'dark:text-zinc-400' : 'dark:text-gray-400',
            )}
          >
            <span className="font-medium">
              <T>widget_market_dynamic_gauge_chart_good_buyers</T>
            </span>
            <span className="font-medium">
              <T>widget_market_dynamic_gauge_chart_good_sellers</T>
            </span>
          </div>
        </div>
      </div>
    </WrapperWidget>
  );
}
